import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2, ArrowRight, ArrowLeft, CheckCircle2, Mail, AlertCircle, Sparkles, ShieldCheck } from "lucide-react";
import { useAuth } from "./AuthContext";
import { useGoogleAuth } from "./useGoogleAuth";
import { adPixels } from "../services/adPixels";
import { GoogleConfigModal } from "./GoogleConfigModal";
import { formatAuthErrorMessage } from "./authErrorUtils";
import { OtpCodeInput } from "../components/ui/OtpCodeInput";

interface RegisterPageProps {
  onGoLogin: () => void;
  isModal?: boolean;
  onClose?: () => void;
}

function PasswordStrength({ password }: { password: string }) {
  if (!password) return null;
  const isValidLength = password.length >= 8;
  const isStrong = isValidLength && (/[A-Z0-9!@#$%^&*]/.test(password) || password.length >= 10);
  const score = !isValidLength ? 1 : isStrong ? 3 : 2;
  const barColors = ["bg-amber-400", "bg-emerald-400", "bg-emerald-500"];

  return (
    <div className="space-y-1.5 mt-1.5">
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-all ${
              i < score ? barColors[score - 1] : "bg-slate-200"
            }`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-[11px]">
        <span className={`flex items-center gap-1 font-medium ${isValidLength ? "text-emerald-600" : "text-slate-500"}`}>
          <CheckCircle2 className={`w-3 h-3 ${isValidLength ? "text-emerald-600" : "text-slate-300"}`} />
          {isValidLength ? "Longueur valide (8+ caractères)" : `Encore ${Math.max(0, 8 - password.length)} caractère(s) minimum`}
        </span>
      </div>
    </div>
  );
}

export function RegisterPage({ onGoLogin, isModal, onClose }: RegisterPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { register, verifyRegistration, resendRegistrationOtp, isLoading, error, clearError } = useAuth();

  const getTargetRedirect = useCallback(() => {
    const fromState = (location.state as any)?.from;
    if (fromState) {
      if (typeof fromState === 'string') return fromState;
      if (fromState.pathname) {
        return `${fromState.pathname}${fromState.search || ''}${fromState.hash || ''}`;
      }
    }
    const queryRedirect = searchParams.get('redirect') || searchParams.get('returnTo');
    if (queryRedirect && queryRedirect.startsWith('/')) {
      return queryRedirect;
    }
    return '/dashboard';
  }, [location.state, searchParams]);
  const {
    triggerGoogleLogin,
    isGoogleLoading,
    googleError,
    clearGoogleError,
    showConfigModal,
    setShowConfigModal
  } = useGoogleAuth();

  const [step, setStep] = useState<"form" | "verify">("form");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Détection des navigateurs intégrés (Facebook, Instagram, TikTok) où les popups Google OAuth sont souvent bloquées
  const isInAppBrowser = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return /FBAN|FBAV|Instagram|BytedanceWebview|TikTok|Musical_ly/i.test(navigator.userAgent || "");
  }, []);

  // OTP Verification State (fallback si activé)
  const [otpCode, setOtpCode] = useState("");
  const [resendTimer, setResendTimer] = useState(60);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Nettoyer toute erreur au montage ou démontage de la page d'inscription, et forcer le scroll en haut
  useEffect(() => {
    if (!isModal) {
      window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
    clearError();
    clearGoogleError();
    setLocalError(null);
    return () => {
      clearError();
      clearGoogleError();
      setLocalError(null);
    };
  }, [clearError, clearGoogleError, isModal]);

  // Compte à rebours de renvoi du code
  useEffect(() => {
    let timer: any;
    if (step === "verify" && resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [step, resendTimer]);

  const handleGoLogin = () => {
    clearError();
    clearGoogleError();
    setLocalError(null);
    onGoLogin();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    clearError();
    clearGoogleError();

    if (password.length < 8) {
      setLocalError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    try {
      const result = await register(email.trim(), password, fullName.trim());
      if (result && result.requiresVerification) {
        setStep("verify");
        setResendTimer(60);
        setOtpCode("");
      } else {
        // Connexion immédiate en mode découverte : tracking pixel immédiat
        adPixels.track("CompleteRegistration", { method: "email" });
        if (onClose) onClose();
        navigate(getTargetRedirect(), { replace: true });
      }
    } catch {
      // L'erreur est gérée dans AuthContext (error)
    }
  };

  const handleVerifyOtp = async (codeToVerify?: string) => {
    const code = codeToVerify || otpCode;
    if (!code || code.length !== 6) {
      setLocalError("Veuillez saisir le code à 6 chiffres.");
      return;
    }
    setLocalError(null);
    clearError();
    try {
      await verifyRegistration(email, code);
      adPixels.track("CompleteRegistration", { method: "email" });
      if (onClose) onClose();
      navigate(getTargetRedirect(), { replace: true });
    } catch {
      // L'erreur est gérée dans AuthContext
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0 || isResending) return;
    setIsResending(true);
    setResendSuccess(false);
    setLocalError(null);
    clearError();
    try {
      await resendRegistrationOtp(email);
      setResendTimer(60);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err: any) {
      setLocalError(err.message || "Erreur lors du renvoi du code.");
    } finally {
      setIsResending(false);
    }
  };

  const handleGoogleSignup = async () => {
    setLocalError(null);
    clearError();
    clearGoogleError();
    try {
      const result = await triggerGoogleLogin();
      adPixels.track("CompleteRegistration", { method: "google" });
      if (result && !result.requires2FA) {
        if (onClose) onClose();
        navigate(getTargetRedirect(), { replace: true });
      }
    } catch {
      // L'erreur est gérée dans useGoogleAuth
    }
  };

  const displayError = formatAuthErrorMessage(localError || googleError || error, "");

  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className={isModal ? "w-full" : "min-h-screen min-h-[100dvh] w-full flex flex-col justify-center items-center bg-white px-4 sm:px-10 py-6 sm:py-10"}>
      <div className={isModal ? "w-full space-y-4" : "w-full max-w-[400px] my-auto space-y-7 py-2"}>
        {/* Logo (uniquement en pleine page) */}
        {!isModal && (
          <div className="text-center flex flex-col items-center">
            <Link to="/" state={{ manualNav: true }} className="inline-block cursor-pointer transition-transform hover:scale-[1.02]">
              <img src="/logo.webp" alt="Zeyilo" className="h-10 sm:h-11 w-auto object-contain mb-1.5" />
            </Link>
            <p className="text-slate-500 text-sm">Agent IA WhatsApp</p>
          </div>
        )}

        {step === "form" ? (
          <div className="space-y-2.5 animate-in fade-in duration-200">
            {/* Header Bento : Uniquement en pleine page, masqué dans le modal compact */}
            {!isModal && (
              <div className="bg-slate-100/80 rounded-2xl p-3.5 sm:p-4 border-0 shadow-none">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white text-emerald-600 flex items-center justify-center shrink-0 border-0 shadow-none">
                    <Sparkles className="w-5 h-5 shrink-0" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h1 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
                      Créer votre compte gratuit
                    </h1>
                    <p className="text-[11px] text-slate-600 mt-0.5 leading-snug">
                      Accès immédiat au tableau de bord • 50 réponses IA offertes
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Bento Card sans border : Formulaire ultra-rapide & Google */}
            <div className={`p-3.5 sm:p-4 border-0 shadow-none space-y-2.5 ${isModal ? 'bg-slate-50/80 rounded-2xl' : 'bg-white rounded-xl sm:rounded-2xl'}`}>
              {/* Si navigateur classique (hors WebView pub), afficher Google en haut */}
              {!isInAppBrowser && (
                <>
                  <button
                    type="button"
                    onClick={handleGoogleSignup}
                    disabled={isLoading || isGoogleLoading}
                    className="w-full flex items-center justify-center gap-2.5 bg-white hover:bg-slate-100/80 disabled:opacity-60 disabled:cursor-not-allowed text-slate-800 font-semibold text-xs sm:text-sm py-2 px-3.5 rounded-xl transition-all cursor-pointer shadow-2xs border-0 active:scale-[0.99]"
                  >
                    {isGoogleLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin text-slate-600" />
                    ) : (
                      <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    )}
                    <span>{isGoogleLoading ? "Connexion à Google..." : "Continuer avec Google"}</span>
                  </button>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-slate-200/60" />
                    <span className="text-[10px] text-slate-400 font-medium">ou inscription rapide en 15s</span>
                    <div className="flex-1 h-px bg-slate-200/60" />
                  </div>
                </>
              )}

              {/* Formulaire 3 champs */}
              <form onSubmit={handleSubmit} className="space-y-2">
                {displayError && (
                  <div className="p-2 bg-rose-50 text-rose-700 text-xs font-medium rounded-xl flex items-center gap-2 border-0 animate-in fade-in">
                    <AlertCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                    <span>{displayError}</span>
                  </div>
                )}

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-slate-700">Votre nom *</label>
                  <input
                    type="text"
                    name="name"
                    autoComplete="name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Ex: Kouassi Aïcha"
                    required
                    className="w-full px-3 py-2 bg-white rounded-xl text-xs sm:text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-900 border-0 shadow-2xs transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-slate-700">Adresse e-mail *</label>
                  <input
                    type="email"
                    name="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@exemple.com"
                    required
                    className="w-full px-3 py-2 bg-white rounded-xl text-xs sm:text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-900 border-0 shadow-2xs transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-slate-700">Mot de passe (8 car. min.) *</label>
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      name="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Minimum 8 caractères"
                      required
                      minLength={8}
                      className="w-full px-3 py-2 bg-white rounded-xl text-xs sm:text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-900 border-0 shadow-2xs transition-all pr-9"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  <PasswordStrength password={password} />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-slate-900 hover:bg-black active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-xs sm:text-sm py-2 sm:py-2.5 px-4 rounded-full flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-xs border-0 mt-1"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Ouverture de votre espace...</span>
                    </>
                  ) : (
                    <>
                      <span>Créer mon compte et découvrir</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>

                <p className="text-[9.5px] text-center text-slate-400 leading-tight pt-0.5">
                  En continuant, vous acceptez nos{" "}
                  <Link to="/cgu" className="text-slate-600 underline hover:text-slate-900">
                    CGU
                  </Link>{" "}
                  et notre{" "}
                  <Link to="/confidentialite" className="text-slate-600 underline hover:text-slate-900">
                    Confidentialité
                  </Link>
                  .
                </p>
              </form>

              {/* Si navigateur intégré (Facebook/Instagram/TikTok), bouton Google en option secondaire */}
              {isInAppBrowser && (
                <>
                  <div className="flex items-center gap-3 pt-1">
                    <div className="flex-1 h-px bg-slate-200/60" />
                    <span className="text-[10px] text-slate-400 font-medium">ou</span>
                    <div className="flex-1 h-px bg-slate-200/60" />
                  </div>
                  <button
                    type="button"
                    onClick={handleGoogleSignup}
                    disabled={isLoading || isGoogleLoading}
                    className="w-full flex items-center justify-center gap-2.5 bg-white hover:bg-slate-100/80 disabled:opacity-60 disabled:cursor-not-allowed text-slate-800 font-semibold text-xs sm:text-sm py-2 px-3.5 rounded-xl transition-all cursor-pointer shadow-2xs border-0 active:scale-[0.99]"
                  >
                    {isGoogleLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin text-slate-600" />
                    ) : (
                      <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    )}
                    <span>Continuer avec Google</span>
                  </button>
                </>
              )}
            </div>

            {/* Footer Bento : Connexion (Sans border) */}
            <div className="bg-slate-100/70 rounded-xl p-2 border-0 shadow-none text-center text-xs text-slate-600">
              <span>Déjà un compte ? </span>
              <button
                type="button"
                onClick={handleGoLogin}
                className="text-slate-900 hover:text-black font-bold underline transition-colors cursor-pointer"
              >
                Se connecter
              </button>
            </div>
          </div>
        ) : (
          /* STEP 2 (Fallback): Vérification de l'adresse e-mail */
          <div className="space-y-3 sm:space-y-3.5 animate-in fade-in slide-in-from-right-3 duration-200">
            <div className="bg-slate-100/80 rounded-2xl p-3.5 sm:p-4 border-0 shadow-none">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white text-emerald-600 flex items-center justify-center shrink-0 border-0 shadow-none">
                  <Mail className="w-5 h-5 shrink-0" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
                    Vérification de l'adresse e-mail
                  </h2>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                    Code de sécurité à 6 chiffres envoyé pour valider votre compte
                  </p>
                </div>
              </div>

              {email && (
                <div className="mt-2.5 pt-2.5 border-t border-slate-200/50 flex items-center justify-between text-[11px]">
                  <span className="text-slate-500">Compte à vérifier :</span>
                  <span className="font-semibold text-slate-800 truncate max-w-[210px]">{email}</span>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl p-3.5 sm:p-4 border-0 shadow-none space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-bold text-slate-700">Code de vérification</span>
                <span className="text-[10px] text-slate-400">Valide 24h</span>
              </div>

              {displayError && (
                <div className="p-2.5 bg-rose-50 text-rose-700 text-xs font-medium rounded-xl flex items-center gap-2 border-0 animate-in fade-in">
                  <AlertCircle className="w-4 h-4 text-rose-500 shrink-0" />
                  <span>{displayError}</span>
                </div>
              )}

              {resendSuccess && (
                <div className="p-2.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-xl flex items-center gap-2 border-0 animate-in fade-in">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>Un nouveau code de vérification vous a été envoyé !</span>
                </div>
              )}

              <OtpCodeInput
                value={otpCode}
                onChange={setOtpCode}
                onComplete={(code) => handleVerifyOtp(code)}
                disabled={isLoading}
                hasError={!!displayError}
                autoFocus={true}
              />

              <div className="text-center text-xs pt-0.5">
                <span className="text-slate-500">Vous n'avez pas reçu le code ? </span>
                {resendTimer > 0 ? (
                  <span className="font-semibold text-slate-700">
                    Renvoyer le code (dans {formatTimer(resendTimer)})
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={isResending}
                    className="font-semibold text-emerald-600 hover:text-emerald-700 hover:underline transition-colors inline-flex items-center gap-1 cursor-pointer disabled:opacity-50"
                  >
                    {isResending ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      "Renvoyer le code"
                    )}
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => handleVerifyOtp()}
                disabled={isLoading || otpCode.length !== 6}
                className="w-full bg-slate-900 hover:bg-slate-800 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs sm:text-sm py-2.5 sm:py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs border-0"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Activation du compte...</span>
                  </>
                ) : (
                  <>
                    <span>Vérifier et continuer</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>

            <div className="space-y-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  setStep("form");
                  setLocalError(null);
                  clearError();
                }}
                className="w-full py-2.5 px-3 rounded-xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 text-xs font-semibold transition-all cursor-pointer flex items-center justify-center gap-1.5 border border-slate-200/70 shadow-xs active:scale-[0.99]"
              >
                <ArrowLeft className="w-3.5 h-3.5 text-slate-400" />
                <span>Modifier l'adresse e-mail</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal Configuration Google Auth (Si Client ID manquant) */}
      <GoogleConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
      />
    </div>
  );
}
